import { useLandingPageTranslation } from '@components/goals/goal-landing-page/useLandingPageTranslation';
import GoalProgressBar from '@components/progress/GoalProgressBar';
import UserAvatar from '@components/shared/UserAvatar';
import { MediaitemService } from '@services/mediaitems';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { DEFAULT_CURRENCY, UIPaths } from 'lib/consts';
import {
  GoalModel,
  GoalSellingStatus,
  hasValidPackageDiscount,
  isAcquiredViaEnroll,
  isGoalOwned
} from 'lib/models/goals';
import { GoalSubscriptionModel } from 'lib/models/goalSubscriptions';
import { BasicGoalSubscriptionModel, GoalSubscriptionStatus } from 'lib/models/shared';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import GoalActions from './GoalActions';
import GoalAuthor from './GoalAuthor';
import GoalLogo from './GoalLogo';
import GoalNewContentChip from './GoalNewContentChip';
import GoalPreSaleReleaseChip from './GoalPreSaleReleaseChip';
import GoalSellingStatusBanner from './GoalSellingStatusBanner';
import GoalSubscriptionStatusChip from './GoalSubscriptionStatusChip';

const formatPrice = (minorUnits: number) => (minorUnits / 100).toFixed(2).replace(/\.00$/, '');

interface GoalCardProps extends React.HTMLAttributes<HTMLDivElement> {
  embed?: boolean;
  goal: GoalModel;
  goalSubscription?: GoalSubscriptionModel | BasicGoalSubscriptionModel | null;
  showPrice?: boolean;
  discountPercentage?: number;
  showActions?: {
    versionType?: boolean;
    deadline?: boolean;
    priority?: boolean;
    like?: boolean;
    conversation?: boolean;
    share?: boolean;
    more?: boolean;
    edit?: boolean;
  };
}

export default function RedesignedGoalCard({
  embed = false,
  goal,
  goalSubscription,
  showPrice = false,
  discountPercentage,
  showActions = {
    versionType: true,
    like: false,
    share: true,
    more: false,
    edit: false
  },
  className = '',
  ...props
}: GoalCardProps) {
  const { t } = useLandingPageTranslation();
  const subscriptionProgress = goalSubscription?.progress;
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const href = useMemo(() => {
    if (goalSubscription && goalSubscription.status === GoalSubscriptionStatus.PENDING_ACTIVE) {
      return UIPaths.paymentsThankYou(goal.entityId);
    } else if (goalSubscription) {
      return UIPaths.assignments(goal.entityId, goalSubscription.id);
    } else {
      return UIPaths.goal(goal.entityId, goal.versionType);
    }
  }, [goalSubscription?.id, goalSubscription?.status, goal.entityId, goal.versionType]);

  const hasSubscription = !!goalSubscription;
  const thumbnailSrc = useMemo(() => {
    if (goal.thumbnailMediaitem) {
      const mediaitemService = new MediaitemService(goal.thumbnailMediaitem);
      return mediaitemService.getImageSrc(true);
    }
    return null;
  }, [goal.thumbnailMediaitem]);

  // Reset loaded state when the image source changes so we show the logo until the new image loads.
  useEffect(() => {
    setThumbnailLoaded(false);
  }, [thumbnailSrc]);

  const currentPriceAmount = goal.currentPrice?.amount;
  const priceCurrency = goal.currentPrice?.currency ?? DEFAULT_CURRENCY;
  const hasPackageDiscount =
    typeof currentPriceAmount === 'number' && hasValidPackageDiscount(discountPercentage);
  const discountedPriceAmount = hasPackageDiscount
    ? Math.floor(currentPriceAmount * (1 - discountPercentage / 100))
    : currentPriceAmount;
  const savedAmount = hasPackageDiscount ? (currentPriceAmount - discountedPriceAmount!) / 100 : 0;
  const isOwned = isGoalOwned(goal, goalSubscription);
  const renderPrice = showPrice && !isOwned && typeof currentPriceAmount === 'number';
  const isPreSale = goal.sellingStatus === GoalSellingStatus.PRE_SALE && !!goal.releaseDate;

  const CardContent = (
    <div
      className={classNames(
        'max-w-[280px] w-[calc(100vw-2rem)] sm:w-[280px] rounded-sm flex flex-col overflow-hidden border-[1px]',
        showPrice ? 'h-[400px]' : 'h-[316px]',
        setupForDark('bg-secondary border-primary'),
        className
      )}
      {...props}
    >
      <div className='relative w-full flex-1 min-h-0 overflow-hidden'>
        {thumbnailSrc && (
          <Image
            src={thumbnailSrc}
            alt={goal.title ?? 'Goal thumbnail'}
            fill
            className='object-cover'
            loading='lazy'
            sizes='280px'
            onLoad={() => setThumbnailLoaded(true)}
            onError={() => setThumbnailLoaded(false)}
          />
        )}

        {showPrice && isOwned && (
          <div
            className={classNames('absolute inset-0 z-[1]', setupForDark('bg-black bg-opacity-50'))}
          />
        )}

        {/* Selling-status ribbon and status chips are hidden inside the package landing page. */}
        {!showPrice && (
          <GoalSellingStatusBanner
            sellingStatus={goal.sellingStatus}
            entityCreatedAt={goal.entityCreatedAt}
          />
        )}

        <div className='absolute top-[10px] left-[13px] flex items-center justify-between w-[255px] h-[28px] z-10'>
          <div className='flex flex-wrap justify-start items-center gap-xxs'>
            {!showPrice && hasSubscription && goalSubscription?.status && (
              <GoalSubscriptionStatusChip status={goalSubscription.status} />
            )}
            {!showPrice && hasSubscription && goalSubscription?.hasNewContent && (
              <GoalNewContentChip />
            )}
            {!showPrice && !hasSubscription && isAcquiredViaEnroll(goal) && (
              <GoalSubscriptionStatusChip status={GoalSubscriptionStatus.ACQUIRED} />
            )}
          </div>
          <div className='flex justify-end items-center'>
            <GoalActions goal={goal} showActions={showActions} />
          </div>
        </div>

        <div className='absolute bottom-0 left-0 right-0 z-10 flex flex-col'>
          {isPreSale && (
            <div className='px-sm pb-xs'>
              <GoalPreSaleReleaseChip releaseDate={goal.releaseDate!} />
            </div>
          )}
          <div
            className={classNames(
              'flex items-center gap-md px-sm py-xs',
              setupForDark('bg-primary bg-opacity-80')
            )}
          >
            <GoalLogo logoSvg={goal.logoSvg} size={32} status={goalSubscription?.status} />
            <div className={classNames('P4B flex-1 min-w-0', setupForDark('text-primary'))}>
              {goal.title}
            </div>
          </div>
        </div>
      </div>

      {goalSubscription && (
        <GoalProgressBar
          goalEntityId={goal.entityId}
          progress={subscriptionProgress}
          height={6}
          className='rounded-none'
        />
      )}

      {showPrice && isOwned && (
        <div
          className={classNames(
            'flex items-center justify-center px-sm py-sm',
            setupForDark('bg-tertiary border-primary')
          )}
        >
          <span
            className={classNames(
              'text-[20px] font-semibold leading-[1.4] text-center',
              setupForDark('text-primary')
            )}
          >
            {t('domains.goals.landingPage.pricing.alreadyOwned')}
          </span>
        </div>
      )}

      {renderPrice && (
        <div className={classNames('flex flex-col gap-xxs px-sm py-xs', setupForDark('bg-white'))}>
          <div className='flex items-baseline gap-xs flex-wrap'>
            {hasPackageDiscount && (
              <span className={classNames('P3R line-through', setupForDark('text-icon-secondary'))}>
                {formatPrice(currentPriceAmount!)} {priceCurrency}
              </span>
            )}
            <span className={classNames('P1B', setupForDark('text-black'))}>
              {formatPrice(discountedPriceAmount!)} {priceCurrency}
            </span>
          </div>
          {hasPackageDiscount && (
            <div className={classNames('P4R', setupForDark('text-icon-secondary'))}>
              {t('domains.goals.landingPage.pricing.youSave', {
                amount: savedAmount,
                percentage: discountPercentage
              })}
            </div>
          )}
        </div>
      )}

      <div className={classNames('p-xs', setupForDark('bg-secondary'))}>
        <div className='flex gap-sm items-center'>
          <UserAvatar
            pictureUrl={goal.user?.pictureUrl}
            size={32}
            isProducer={true}
            isChatbot={false}
            isMaster={false}
          />
          <GoalAuthor
            goal={goal}
            className={classNames('!LS2M uppercase', setupForDark('!text-secondary'))}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Link
      href={href}
      target={embed ? '_blank' : undefined}
      className='block hover:transform hover:scale-105 transition-transform duration-200'
    >
      {CardContent}
    </Link>
  );
}
